import { useEffect, type ReactNode } from 'react'
import './BottomSheet.css'

/**
 * The mobile bottom sheet.
 *
 * The design uses this shape three times — the account menu, the Skida PRO
 * section switcher and the Marketing section switcher — with identical
 * geometry each time: a scrim over the whole shell, a panel anchored to
 * `bottom: 58px` so it sits *on top of* the tab bar rather than under it, and
 * the top two corners rounded.
 *
 * That 58px is the tab bar's height and it is the one number in here worth
 * naming. A sheet flush to the bottom would cover the bar, which is where you
 * are about to tap next; a sheet under it would put its last item behind a row
 * of buttons. Sitting exactly above it keeps both reachable.
 */
export function BottomSheet({
  title,
  onClose,
  children,
}: {
  /** The uppercase group label at the top. Omitted for the account sheet. */
  title?: string
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="sheet__scrim" onClick={onClose}>
      {/* Clicks inside the panel must not reach the scrim, or picking an item
          would close the sheet twice — once for the choice and once for the
          dismissal, and on a slow render you would see it flicker. */}
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {title && <div className="sheet__title">{title}</div>}
        {children}
      </div>
    </div>
  )
}

/** One row in a sheet, with an optional count beside it. */
export function SheetItem({
  label,
  active,
  danger,
  badge,
  onClick,
}: {
  label: string
  active?: boolean
  danger?: boolean
  badge?: number | null
  onClick: () => void
}) {
  return (
    <button
      className={`sheet__item${active ? ' sheet__item--active' : ''}${
        danger ? ' sheet__item--danger' : ''
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      {badge ? <span className="sheet__badge">{badge}</span> : null}
    </button>
  )
}

/**
 * The control that opens a section sheet: current section, and "▾ Switch".
 *
 * It replaces the horizontal tab row, which is the part of the desktop layout
 * that fails worst on a phone — six tabs at 150px each need 900px, so they
 * either wrap into three rows of chrome above every screen or scroll sideways
 * with half of them permanently out of sight.
 */
export function SectionSwitch({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="sectionswitch" onClick={onClick}>
      <span className="sectionswitch__label">{label}</span>
      <span className="sectionswitch__hint">▾ Switch</span>
    </button>
  )
}
